import React, { useState } from 'react';
import { portfolioItems, websiteItems } from '../data/mock';
import { Play, ArrowRight } from 'lucide-react';

const SelectedWork = () => {
  const [playingVideo, setPlayingVideo] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);

  const handlePlayClick = (videoId) => {
    setPlayingVideo(videoId);
  };

  return (
    <section id="work" className="bg-[#0B0D10] pt-0 pb-24 lg:pb-32" aria-labelledby="selected-work-heading">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Section Title */}
        <div className="flex items-center gap-4 mb-6">
          <h2 id="selected-work-heading" className="text-2xl lg:text-3xl font-semibold text-[#F2F4F7]">
            Selected Work
          </h2>
          <div className="flex-1 h-px bg-white/10" aria-hidden="true" />
        </div>

        {/* Section Description */}
        <p className="text-[#A6AFBD] text-base lg:text-lg leading-relaxed mb-10 max-w-3xl">
          I produce high-end commercials and brand experiences—often delivered in days, not weeks—using modern, AI-native workflows built for speed and scale.
        </p>

        {/* Video Portfolio Grid - 3 per row on desktop (2 rows), stacked on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolioItems.map((item) => (
            <article
              key={item.id}
              className="group relative overflow-hidden"
            >
              {/* Video Container */}
              <div className="relative aspect-[4/3] overflow-hidden bg-[#11151B]">
                {playingVideo === item.videoId ? (
                  /* YouTube Embed - Privacy Enhanced with Lazy Loading */
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${item.videoId}?autoplay=1&rel=0&modestbranding=1&showinfo=0`}
                    title={`${item.title} - ${item.type} by Ramon Govea`}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                ) : (
                  /* Thumbnail with Play Button */
                  <div
                    className="relative w-full h-full cursor-pointer"
                    onClick={() => handlePlayClick(item.videoId)}
                    onMouseEnter={() => setHoveredItem(`video-${item.id}`)}
                    onMouseLeave={() => setHoveredItem(null)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handlePlayClick(item.videoId)}
                    aria-label={`Play ${item.title} video`}
                  >
                    {/* YouTube Thumbnail - using sddefault for reliable quality */}
                    <img
                      src={`https://img.youtube.com/vi/${item.videoId}/sddefault.jpg`}
                      alt={`${item.title} - ${item.type} produced by Ramon Govea`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        // Fallback chain: sddefault -> hqdefault -> mqdefault
                        if (e.target.src.includes('sddefault')) {
                          e.target.src = `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`;
                        } else if (e.target.src.includes('hqdefault')) {
                          e.target.src = `https://img.youtube.com/vi/${item.videoId}/mqdefault.jpg`;
                        }
                      }}
                    />
                    {/* Hover Overlay with Play Button */}
                    <div className={`absolute inset-0 bg-[#0B0D10]/50 flex items-center justify-center transition-opacity duration-300 ${
                      hoveredItem === `video-${item.id}` ? 'opacity-100' : 'opacity-0'
                    }`} aria-hidden="true">
                      <div className="w-16 h-16 rounded-full bg-[#3B82F6]/90 flex items-center justify-center transform transition-transform duration-300 hover:scale-110">
                        <Play size={24} className="text-white ml-1" fill="white" />
                      </div>
                    </div>
                    {/* Persistent subtle play indicator */}
                    <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                      hoveredItem === `video-${item.id}` ? 'opacity-0' : 'opacity-100'
                    }`} aria-hidden="true">
                      <div className="w-14 h-14 rounded-full bg-[#0B0D10]/60 backdrop-blur-sm flex items-center justify-center">
                        <Play size={22} className="text-white/90 ml-1" fill="white" fillOpacity={0.9} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Caption */}
              <div className="mt-4">
                <p className="text-[#F2F4F7] font-medium">
                  {item.title}
                  <span className="text-[#A6AFBD] font-normal"> — {item.type}</span>
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Work With Me CTA Button */}
        <div className="flex justify-center mt-12">
          <a
            href="/work-with-me"
            className="group inline-flex items-center gap-3 bg-[#3B82F6] text-white px-7 py-4 text-base font-medium transition-all duration-300 hover:bg-[#2563EB] hover:scale-[1.02] hover:shadow-lg hover:shadow-[#3B82F6]/25"
          >
            Work With Me
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>

        {/* Website Portfolio Grid - 4 in a row on desktop, stacked on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {websiteItems.map((item) => (
            <article
              key={`website-${item.id}`}
              className="group relative overflow-hidden"
            >
              {/* Website Screenshot Container */}
              <div className="relative aspect-[4/3] overflow-hidden bg-[#11151B]">
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full"
                  onMouseEnter={() => setHoveredItem(`website-${item.id}`)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <img
                    src={item.image}
                    alt={`${item.title} - ${item.type} by Ramon Govea`}
                    className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${item.imagePosition || 'object-top'}`}
                    loading="lazy"
                  />
                  {/* Hover Overlay */}
                  <div className={`absolute inset-0 bg-[#0B0D10]/30 transition-opacity duration-300 ${
                    hoveredItem === `website-${item.id}` ? 'opacity-100' : 'opacity-0'
                  }`} aria-hidden="true" />
                </a>
              </div>
              {/* Caption */}
              <div className="mt-4">
                <p className="text-[#F2F4F7] font-medium">
                  {item.title}
                  <span className="text-[#A6AFBD] font-normal"> — {item.type}</span>
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SelectedWork;
